import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MessageSquare, Bug, Lightbulb, Heart } from "lucide-react";
const Feedback = () => {
  const {
    user
  } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    category: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const categories = [{
    value: "bug",
    label: "Bug Report",
    icon: Bug,
    color: "text-red-500"
  }, {
    value: "feature",
    label: "Feature Request",
    icon: Lightbulb,
    color: "text-yellow-500"
  }, {
    value: "feedback",
    label: "General Feedback",
    icon: MessageSquare,
    color: "text-blue-500"
  }, {
    value: "praise",
    label: "Praise & Thanks",
    icon: Heart,
    color: "text-green-500"
  }];
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.from('feedback').insert({
        name: formData.name,
        email: formData.email,
        category: formData.category,
        subject: formData.subject,
        message: formData.message,
        user_id: user?.id || null
      }).select().single();
      
      if (error) {
        console.error('Error submitting feedback:', error);
        toast.error("Failed to submit feedback. Please try again.");
        return;
      }

      // Send admin notification via edge function
      try {
        await supabase.functions.invoke('notify-feedback', {
          body: {
            type: 'INSERT',
            table: 'feedback',
            record: data
          }
        });
      } catch (notifyError) {
        console.error('Failed to send notification:', notifyError);
        // Don't fail the submission if notification fails
      }

      toast.success("Thank you for your feedback! We'll get back to you soon.", {
        description: "Your input helps us improve Airing Down for everyone."
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        category: "",
        subject: "",
        message: ""
      });
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  return <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-mountain text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Help Us Build
            <span className="text-primary-glow"> The Future</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Your feedback drives our development. Report bugs, suggest features, or share your thoughts 
            on how we can improve your off-road experience.
          </p>
        </div>
      </section>

      {/* Feedback Form */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {categories.map(category => {
              const IconComponent = category.icon;
              return <Card key={category.value} className="cursor-pointer hover:shadow-lg transition-all duration-300">
                    <CardHeader className="text-center pb-4">
                      <div className={`mx-auto w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${category.color} bg-muted`}>
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <CardTitle className="text-lg">{category.label}</CardTitle>
                      <CardDescription>
                        {category.value === 'bug' && 'Found something that isn\'t working right?'}
                        {category.value === 'feature' && 'Have an idea to make Airing Down better?'}
                        {category.value === 'feedback' && 'Share your thoughts and suggestions with us.'}
                        {category.value === 'praise' && 'Love something? We\'d love to hear about it!'}
                      </CardDescription>
                    </CardHeader>
                  </Card>;
            })}
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-2xl">Share Your Feedback</CardTitle>
                <CardDescription>
                  We read every piece of feedback and use it to improve the platform. 
                  Your input matters to our community.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input id="name" value={formData.name} onChange={e => handleInputChange('name', e.target.value)} placeholder="Your name" required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input id="email" type="email" value={formData.email} onChange={e => handleInputChange('email', e.target.value)} placeholder="your@email.com" required />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select value={formData.category} onValueChange={value => handleInputChange('category', value)} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select feedback type" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(category => <SelectItem key={category.value} value={category.value}>
                            {category.label}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Input id="subject" value={formData.subject} onChange={e => handleInputChange('subject', e.target.value)} placeholder="Brief description of your feedback" required />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <Textarea id="message" value={formData.message} onChange={e => handleInputChange('message', e.target.value)} placeholder="Tell us more about your feedback, bug report, or feature request..." className="min-h-[120px]" required />
                  </div>

                  <Button type="submit" size="lg" variant="hero" className="w-full md:w-auto" disabled={isSubmitting}>
                    {isSubmitting ? "Sending..." : "Send Feedback"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

    </div>;
};
export default Feedback;